import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import type { IPortfolioCodeQualityAggregate } from '@skystone/shared';

interface QgPassRateTileProps {
  aggregate: IPortfolioCodeQualityAggregate;
}

/**
 * Verdict-strip tile #1 — Quality Gate pass rate.
 *
 * Reads `qgPassed / qgTotal` from the aggregate. Headline is "M / N" so the
 * raw count is always visible; the percentage shows underneath as the
 * eyeballed comparison metric.
 *
 * No delta in v1 — priorAggregate is null until alert_status lands in
 * quality_history (bd skystone-pk3b). Once prior is wired the delta will
 * land here as a third paragraph.
 */
export function QgPassRateTile({ aggregate }: Readonly<QgPassRateTileProps>) {
  const { qgPassed, qgTotal } = aggregate;
  const pct = qgTotal === 0 ? null : (qgPassed / qgTotal) * 100;

  return (
    <Card className="shadow-none" data-testid="cq-qg-pass-rate-tile">
      <CardContent className="p-4">
        <div className="flex items-center gap-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Quality Gate
          </p>
          <MetricInfoPopover metricKey="cq_qg_pass_rate" />
        </div>
        <p className="mt-2 text-2xl font-bold leading-none text-foreground">
          {qgTotal === 0 ? '—' : `${qgPassed} / ${qgTotal}`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {pct === null ? 'No analysed repos' : `${pct.toFixed(1)}% passing`}
        </p>
      </CardContent>
    </Card>
  );
}
